import React from 'react';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import FailureDetailsSection from './FailureDetailsSection';
import FailuresByFlowTable from './FailuresByFlowTable';
import MetricCard from './MetricCard';
import RepeatedFailuresGroupedTable from './RepeatedFailuresGroupedTable';
import TopFailingFlowsTable from './TopFailingFlowsTable';
import { isObject, numberOrZero } from './aiReportUtils';
import { reportStyles } from './styles';

const getFailureDetails = (breakdown) => {
  if (!isObject(breakdown)) return [];

  return Object.entries(breakdown)
    .filter(([, details]) => isObject(details))
    .map(([type, details]) => ({
      type,
      count: numberOrZero(details.count),
      events: Array.isArray(details.events) ? details.events : [],
      attributes: Array.isArray(details.attributes) ? details.attributes : [],
    }))
    .filter((details) => details.events.length > 0 || details.attributes.length > 0);
};

export default function RegressionSummaryReport({ report = {}, regressionId }) {
  const overallCounts = isObject(report.overall_counts) ? report.overall_counts : {};
  const passed = numberOrZero(overallCounts.passed);
  const failed = numberOrZero(overallCounts.failed);
  const missing = numberOrZero(overallCounts.missing);
  const totalEvents = passed + failed + missing;
  const passRate = totalEvents > 0 ? ((passed / totalEvents) * 100).toFixed(1) : '0.0';
  const failureDetails = getFailureDetails(report.failure_type_breakdown);
  const topFailingFlows = Array.isArray(report.top_failing_flows) ? report.top_failing_flows : [];
  const failuresByFlow = Array.isArray(report.failures_by_flow) ? report.failures_by_flow : [];
  const repeatedFailuresGrouped = Array.isArray(report.repeated_failures_grouped)
    ? report.repeated_failures_grouped
    : [];

  return (
    <Box>
      <Box sx={reportStyles.sectionHeader}>
        <Typography variant="h6" sx={reportStyles.sectionTitle}>
          Regression {regressionId}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          AI-generated regression report based on saved validation results.
        </Typography>
      </Box>

      <Grid container spacing={2} sx={reportStyles.metricsGrid}>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            label="Total Events"
            value={totalEvents}
            helper={`${passRate}% passed`}
            tone="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            label="Passed"
            value={passed}
            helper="Events validated successfully"
            tone="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            label="Failed"
            value={failed}
            helper="Events with validation failures"
            tone="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            label="Missing"
            value={missing}
            helper="Expected events not observed"
            tone="warning"
          />
        </Grid>
      </Grid>

      <Divider sx={reportStyles.sectionDivider} />

      {report.plain_summary && (
        <Paper variant="outlined" sx={reportStyles.reportPaperWithMargin}>
          <Typography variant="subtitle1" sx={reportStyles.subsectionTitle}>
            Plain Summary
          </Typography>
          <Typography variant="h7" color="text.secondary" sx={{ mt: 1 }}>
            {report.plain_summary}
          </Typography>
        </Paper>
      )}

      <FailuresByFlowTable failuresByFlow={failuresByFlow} />

      <RepeatedFailuresGroupedTable repeatedFailuresGrouped={repeatedFailuresGrouped} />

      <TopFailingFlowsTable topFailingFlows={topFailingFlows} />

      <FailureDetailsSection failureDetails={failureDetails} />
    </Box>
  );
}
